import useApi, { FetchApiOptions } from '@common/hooks/useApi';
import AnnouncementApiRoutes from '@modules/announcements/defs/api-routes';
import {
  Announcement,
  AnnouncementChannel,
  AnnouncementStats,
  GenerateAllResult,
  GenerateResult,
  GlobalStats,
} from '@modules/announcements/defs/types';
import { useCallback } from 'react';

const useAnnouncements = () => {
  const fetchApi = useApi();

  const idUrl = (route: string, id: number) => route.replace('{id}', id.toString());

  // ─── Admin methods ─────────────────────────────────

  const list = useCallback(
    async (options?: FetchApiOptions) => {
      return fetchApi<{ items: Announcement[] }>(AnnouncementApiRoutes.List, {
        method: 'GET',
        ...options,
      });
    },
    [fetchApi]
  );

  const create = useCallback(
    async (
      data: {
        title: string;
        content_html: string;
        channels: AnnouncementChannel[];
        channel_contents?: Record<string, { title?: string; body?: string; body_html?: string }>;
        segment_ids?: number[] | null;
        excluded_segment_ids?: number[] | null;
      },
      options?: FetchApiOptions
    ) => {
      return fetchApi<{ item: Announcement }>(AnnouncementApiRoutes.Create, {
        method: 'POST',
        data,
        displaySuccess: true,
        ...options,
      });
    },
    [fetchApi]
  );

  const show = useCallback(
    async (id: number, options?: FetchApiOptions) => {
      return fetchApi<{ item: Announcement; stats: AnnouncementStats }>(
        idUrl(AnnouncementApiRoutes.Show, id),
        {
          method: 'GET',
          ...options,
        }
      );
    },
    [fetchApi]
  );

  const publish = useCallback(
    async (id: number, scheduledAt?: string | null, options?: FetchApiOptions) => {
      return fetchApi(idUrl(AnnouncementApiRoutes.Publish, id), {
        method: 'POST',
        data: scheduledAt ? { scheduled_at: scheduledAt } : {},
        displaySuccess: true,
        ...options,
      });
    },
    [fetchApi]
  );

  const cancelSchedule = useCallback(
    async (id: number, options?: FetchApiOptions) => {
      return fetchApi<{ item: Announcement }>(idUrl(AnnouncementApiRoutes.CancelSchedule, id), {
        method: 'POST',
        displaySuccess: true,
        ...options,
      });
    },
    [fetchApi]
  );

  const update = useCallback(
    async (
      id: number,
      data: { title?: string; content_html?: string },
      options?: FetchApiOptions
    ) => {
      return fetchApi<{ item: Announcement }>(idUrl(AnnouncementApiRoutes.Update, id), {
        method: 'PUT',
        data,
        displaySuccess: true,
        ...options,
      });
    },
    [fetchApi]
  );

  const destroy = useCallback(
    async (id: number, options?: FetchApiOptions) => {
      return fetchApi(idUrl(AnnouncementApiRoutes.Delete, id), {
        method: 'DELETE',
        ...options,
      });
    },
    [fetchApi]
  );

  const generate = useCallback(
    async (
      data: { title: string; content_html: string; channel: AnnouncementChannel },
      options?: FetchApiOptions
    ) => {
      return fetchApi<GenerateResult>(AnnouncementApiRoutes.Generate, {
        method: 'POST',
        data,
        ...options,
      });
    },
    [fetchApi]
  );

  const generateAll = useCallback(
    async (
      data: { title: string; content_html: string; channels: AnnouncementChannel[] },
      options?: FetchApiOptions
    ) => {
      return fetchApi<GenerateAllResult>(AnnouncementApiRoutes.GenerateAll, {
        method: 'POST',
        data,
        ...options,
      });
    },
    [fetchApi]
  );

  const uploadImage = useCallback(
    async (file: File, options?: FetchApiOptions) => {
      const formData = new FormData();
      formData.append('image', file);
      return fetchApi<{ url: string }>(AnnouncementApiRoutes.UploadImage, {
        method: 'POST',
        data: formData,
        ...options,
      });
    },
    [fetchApi]
  );

  const getStats = useCallback(
    async (id: number, options?: FetchApiOptions) => {
      return fetchApi<AnnouncementStats>(idUrl(AnnouncementApiRoutes.Stats, id), {
        method: 'GET',
        ...options,
      });
    },
    [fetchApi]
  );

  const getGlobalStats = useCallback(
    async (options?: FetchApiOptions) => {
      return fetchApi<GlobalStats>(AnnouncementApiRoutes.GlobalStats, {
        method: 'GET',
        ...options,
      });
    },
    [fetchApi]
  );

  // ─── User methods (in-app popup) ───────────────────

  const getPending = useCallback(
    async (options?: FetchApiOptions) => {
      return fetchApi<{
        items: Array<{ id: number; title: string; contentHtml: string; publishedAt: string }>;
      }>(AnnouncementApiRoutes.Pending, {
        method: 'GET',
        ...options,
      });
    },
    [fetchApi]
  );

  const getHistory = useCallback(
    async (options?: FetchApiOptions) => {
      return fetchApi<{
        items: Array<{ id: number; title: string; contentHtml: string; publishedAt: string }>;
      }>(AnnouncementApiRoutes.History, {
        method: 'GET',
        ...options,
      });
    },
    [fetchApi]
  );

  const recordInteraction = useCallback(
    async (
      id: number,
      type: 'DISPLAYED' | 'READ' | 'DISMISSED' | 'CLICKED',
      metadata?: Record<string, unknown>,
      options?: FetchApiOptions
    ) => {
      return fetchApi(idUrl(AnnouncementApiRoutes.Interaction, id), {
        method: 'POST',
        data: { type, ...(metadata ? { metadata } : {}) },
        ...options,
      });
    },
    [fetchApi]
  );

  return {
    list,
    create,
    update,
    show,
    publish,
    cancelSchedule,
    destroy,
    generate,
    generateAll,
    uploadImage,
    getStats,
    getGlobalStats,
    getPending,
    getHistory,
    recordInteraction,
  };
};

export default useAnnouncements;
